<template>
  <div
    class="relative m-auto w-[1145px] min-w-[700px] max-w-[98vw] rounded-xl bg-gray-900"
  >
    <TConfirmationModal ref="confirmationModal">
      <template #header>{{ $t("file_manager.warning") }}</template>
      {{ $t("premade_templates.delete_confirm") }}
      <template #confirm-action>{{ $t("premade_templates.delete") }}</template>
    </TConfirmationModal>

    <TButton
      data-testid="close-modal"
      class="!absolute right-[8px] top-[8px]"
      @click="closeTemplatesPicker"
    >
      <template #icon>
        <CloseLarge />
      </template>
    </TButton>

    <div class="flex items-center gap-3 px-5 py-5">
      <div
        class="box-border grid h-[38px] w-[38px] items-center justify-center rounded-md border border-solid border-gray-500 bg-gray-700 text-gray-50"
      >
        <font-awesome-icon :icon="['fas', 'scroll']" />
      </div>
      <div class="text-md font-regular font-inter leading-4 text-gray-100">
        <h1 class="text-base font-semibold text-gray-50">
          {{ data?.name }}
        </h1>
      </div>
    </div>

    <div class="flex items-center justify-between px-5 pb-2">
      <t-button
        data-testid="premade-templates-back-to-list"
        @click="handleBackClick"
      >
        <template #icon>
          <svg
            style="margin-right: 5px"
            width="14"
            height="8"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.5459 5.08007H12.8037C12.9678 5.08007 13.1318 4.94335 13.1318 4.75194V3.22069C13.1318 3.05663 12.9678 2.89257 12.8037 2.89257H4.5459V1.63475C4.5459 1.06054 3.83496 0.759754 3.4248 1.16991L1.07324 3.54882C0.799805 3.79491 0.799805 4.20507 1.07324 4.45116L3.4248 6.83007C3.83496 7.24022 4.5459 6.93944 4.5459 6.36522V5.08007Z"
              fill="#CBCBCD"
            />
          </svg>
        </template>
        {{ $t("file_manager.go_back") }}
      </t-button>
      <TabGroup
        :default-index="isMobileView ? 0 : 1"
        @change="onPreviewTabChanged"
      >
        <TabList class="flex space-x-1 rounded-xl bg-gray-800 p-1">
          <Tab
            v-slot="{ selected }"
            as="div"
            class="flex w-full focus:outline-none"
          >
            <button
              tooltip="mobile"
              data-testid="mobile-first-mobile-button"
              :class="[
                'grid h-8 w-8 items-center justify-center rounded-md font-inter text-xs font-medium leading-5 text-gray-100 transition duration-300',
                selected
                  ? 'bg-gray-600 text-gray-50 outline outline-2 outline-offset-[-2px] outline-accent-1'
                  : 'bg-gray-800 text-gray-100 hover:bg-gray-600 hover:text-gray-50',
              ]"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.3125 13.6875H8.6875C8.44141 13.6875 8.25 13.9062 8.25 14.125C8.25 14.3711 8.44141 14.5625 8.6875 14.5625H11.3125C11.5312 14.5625 11.75 14.3711 11.75 14.125C11.75 13.9062 11.5312 13.6875 11.3125 13.6875ZM13.0625 2.75H6.9375C5.95312 2.75 5.1875 3.54297 5.1875 4.5V15C5.1875 15.9844 5.95312 16.75 6.9375 16.75H13.0625C14.0195 16.75 14.8125 15.9844 14.8125 15V4.5C14.8125 3.54297 14.0195 2.75 13.0625 2.75ZM13.5 15C13.5 15.2461 13.2812 15.4375 13.0625 15.4375H6.9375C6.69141 15.4375 6.5 15.2461 6.5 15V4.5C6.5 4.28125 6.69141 4.0625 6.9375 4.0625H8.25V4.9375C8.25 5.18359 8.44141 5.375 8.6875 5.375H11.3125C11.5312 5.375 11.75 5.18359 11.75 4.9375V4.0625H13.0625C13.2812 4.0625 13.5 4.28125 13.5 4.5V15Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </Tab>
          <Tab
            v-slot="{ selected }"
            as="div"
            class="flex w-full focus:outline-none"
          >
            <button
              tooltip="laptop"
              data-testid="mobile-first-desktop-button"
              :class="[
                'h-8 w-8 rounded-md font-inter text-xs font-medium leading-5 text-gray-100 transition duration-300',
                selected
                  ? 'bg-gray-600 text-gray-50 outline outline-2 outline-offset-[-2px] outline-accent-1'
                  : 'bg-gray-800 text-gray-100 hover:bg-gray-600 hover:text-gray-50',
              ]"
            >
              <font-awesome-icon :icon="['fas', 'laptop']" />
            </button>
          </Tab>
        </TabList>
      </TabGroup>
      <div class="flex items-center gap-2">
        <TButton
          v-if="showDelete"
          danger
          data-testid="premade-templates-delete"
          @click="deletePromiseTemplate"
        >
          <template #icon>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.4688 4.9375C15.8242 4.9375 16.125 5.23828 16.125 5.59375C16.125 5.97656 15.8242 6.25 15.4688 6.25H15.1406L14.4844 15.1367C14.4023 16.0664 13.6641 16.75 12.7344 16.75H7.23828C6.30859 16.75 5.57031 16.0664 5.48828 15.1367L4.83203 6.25H4.53125C4.14844 6.25 3.875 5.97656 3.875 5.59375C3.875 5.23828 4.14844 4.9375 4.53125 4.9375H6.41797L7.42969 3.43359C7.70312 3.02344 8.19531 2.75 8.71484 2.75H11.2578C11.7773 2.75 12.2695 3.02344 12.543 3.43359L13.5547 4.9375H15.4688ZM8.71484 4.0625C8.63281 4.0625 8.55078 4.11719 8.52344 4.17188L8.00391 4.9375H11.9688L11.4492 4.17188C11.4219 4.11719 11.3398 4.0625 11.2578 4.0625H8.71484ZM13.8281 6.25H6.14453L6.80078 15.0547C6.82812 15.2734 7.01953 15.4375 7.23828 15.4375H12.7344C12.9531 15.4375 13.1445 15.2734 13.1719 15.0547L13.8281 6.25Z"
                fill="currentColor"
              />
            </svg>
          </template>
          {{ $t("premade_templates.delete") }}
        </TButton>
        <t-button
          success
          class="mr-5"
          data-testid="premade-templates-use"
          @click="useTemplate"
        >
          {{ $t("premade_templates.use_template") }}
        </t-button>
      </div>
    </div>
    <div
      class="relative rounded-b-xl border-0 border-t border-solid border-gray-500 bg-gray-700 px-5 py-4"
      style="padding: 20px 20px; height: calc(100vh - 180px)"
    >
      <div v-if="!loading" class="grid">
        <iframe
          v-if="data"
          class="mx-auto border-none bg-white"
          style="height: calc(100vh - 180px)"
          :class="[isMobileView ? 'w-[350px]' : 'w-[660px]']"
          :srcdoc="data.html"
        ></iframe>
      </div>

      <div v-else style="padding-top: 16rem">
        <t-loading large></t-loading>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { usePremadeTemplatesStore } from "@/stores/premadeTemplatesStore";
  import premadeTemplates from "@/composables/usePremadeTemplates";
  import { TemplateDetail } from "types/PremadeTemplates/PremadeTemplates";
  import { CloseLarge } from "@topol/icons";
  import { TConfirmationModal, TButton, TLoading } from "@topol/twig-ui";
  import { TabGroup, TabList, Tab } from "@headlessui/vue";
  import { ref, Ref, onMounted, computed } from "vue";
  const { getTemplate, deleteTemplate } = premadeTemplates();

  const props = defineProps({
    teplateId: {
      type: Number,
      required: true,
    },
  });

  const emits = defineEmits(["closeTemplatesPicker", "back", "chooseTemplate"]);

  const data: Ref<TemplateDetail | null> = ref(null);
  const loading = ref(true);
  const isMobileView = ref(false);
  const confirmationModal = ref();

  const mjmlStore = useMjmlStore();
  const premadeTemplatesStore = usePremadeTemplatesStore();

  const showDelete = computed(() => {
    return premadeTemplatesStore.showDelete;
  });

  const closeTemplatesPicker = () => {
    emits("closeTemplatesPicker");
  };

  const handleBackClick = () => {
    emits("back");
  };

  const useTemplate = () => {
    if (data.value) {
      try {
        const template = JSON.parse(data.value.json);
        mjmlStore.takeSnapshot();
        mjmlStore.setMjml(template);
        emits("chooseTemplate");
        closeTemplatesPicker();
      } catch (e) {
        console.error(e);
      }
    }
  };

  function onPreviewTabChanged(index: number) {
    if (index === 0) {
      isMobileView.value = true;
    } else {
      isMobileView.value = false;
    }
  }

  async function deletePromiseTemplate() {
    const confirmation = await confirmationModal.value.open();

    if (confirmation === "cancel") {
      return;
    }

    loading.value = true;

    const isDeleted = await deleteTemplate(props.teplateId);

    loading.value = false;
    if (isDeleted) handleBackClick();
  }

  onMounted(async () => {
    data.value = await getTemplate(props.teplateId);
    loading.value = false;
  });
</script>
